import { useEffect, useState, useMemo } from "react";
import {
  Search, RefreshCw, ChevronDown, ChevronUp,
  Shield, Calendar, CreditCard, Package,
  Users, TrendingUp, AlertTriangle, CheckCircle2,
  Eye, Building2, ChevronLeft, ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { BASE_URL } from "../../../config/api";
import DateRangePicker from "../../../Component/Common/DateRangePicker";
import Pagination from "../../../Component/Common/Pagination";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtCur = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function daysLeft(endDate) {
  if (!endDate) return null;
  const diff = Math.ceil((new Date(endDate) - new Date()) / 86400000);
  return Math.max(diff, 0);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function initials(name = "") {
  return name.split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase() || "?";
}

// ─── ATOMS ────────────────────────────────────────────────────────────────────
function StatusPill({ sub }) {
  const days = daysLeft(sub.end_date);
  const active = sub.status === "1" || sub.status === 1;

  if (!active || days === 0) {
    const expiredDays = sub.end_date
      ? Math.max(
          Math.floor(
            (new Date() - new Date(sub.end_date)) / 86400000
          ),
          0
        )
      : 0;

    return (
      <div>
        <span className="inline-flex items-center gap-1 text-[12px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
          {days === 0 ? "Expired" : "Inactive"}
        </span>

        {expiredDays > 0 && (
          <p className="text-[11px] text-slate-400 mt-1">
            {expiredDays}d ago
          </p>
        )}
      </div>
    );
  }

  if (days <= 7) {
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-500 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Active
      </span>

      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-[2px] rounded-full bg-amber-50 text-amber-600 border border-amber-200 leading-none">
        <AlertTriangle size={8} />
        {days}d left
      </span>
    </div>
  );
}

  return (
    <div className="">
    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-500 border border-emerald-200">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 "/>
      Active
     
    </span> 
     <p className="text-[12px] text-slate-400">
       {days}d left
      </p>
      </div>
  );
}

function BalanceBar({ balance, max }) {
  const pct = max > 0 ? Math.min((balance / max) * 100, 100) : 0;
  const colorClass = pct > 50 ? "text-emerald-500 bg-emerald-500" : pct > 20 ? "text-amber-600 bg-amber-600" : "text-red-500 bg-red-500";
  const textClr = colorClass.split(" ")[0];
  const bgClr = colorClass.split(" ")[1];

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className={`text-[11px] font-black ${textClr}`}>{balance} / {max}</span>
        <span className="text-[12px] text-slate-400">{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-slate-100">
        <div className={`h-full rounded-full transition-all duration-500 ${bgClr}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}

// ─── STAT CARD ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, styles }) {
  return (
    <div className={`rounded-2xl p-4 flex items-center gap-3 border shadow-[0_1px_6px_rgba(0,0,0,0.04)] ${styles.bg} ${styles.border}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${styles.iconBg}`}>
        <Icon size={16} className={styles.text}/>
      </div>
      <div>
        <p className={`text-[22px] font-black leading-none ${styles.text}`}>{value}</p>
        <p className="text-[11px] font-semibold mt-0.5 text-slate-500">{label}</p>
      </div>
    </div>
  );
}

// ─── DETAIL DRAWER ────────────────────────────────────────────────────────────
function DetailDrawer({ sub, onClose }) {
  if (!sub) return null;

  const parseJson = (str) => { try { return JSON.parse(str || "{}"); } catch { return {}; } };
  const payJson = parseJson(sub.payment_json);
  const invJson = parseJson(sub.invoice_json);
  const days = daysLeft(sub.end_date);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md h-full bg-white overflow-y-auto flex flex-col shadow-[-8px_0_32px_rgba(0,0,0,0.15)]">
        
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between sticky top-0 bg-white z-10 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-50">
              <Shield size={16} className="text-blue-600"/>
            </div>
            <div>
              <p className="text-[13px] font-black text-slate-800">Subscription Detail</p>
              <p className="text-[12px] font-semibold text-blue-600">{sub.subscription_code}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-slate-100 transition-colors">
            <ChevronRightIcon size={16} className="text-slate-400"/>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 px-5 py-4 space-y-5">
          <div className="rounded-2xl p-4 space-y-3 bg-blue-50 border border-blue-200">
            <div className="flex items-center justify-between">
              <StatusPill sub={sub}/>
              <span className="text-[11px] font-bold text-blue-600">{days !== null ? `${days} days remaining` : ""}</span>
            </div>
            <BalanceBar balance={sub.balance || 0} max={sub.enroll_max || 0}/>
            <p className="text-[12px] text-slate-500">Enroll balance · {sub.balance} available of {sub.enroll_max} max</p>
          </div>

          <Section title="Retailer" icon={Building2}>
            <Row label="Business" value={sub.retailer?.business_name}/>
            <Row label="Code" value={sub.retailer?.company_code} highlight/>
            <Row label="Contact" value={sub.retailer?.contact_person}/>
            <Row label="Phone" value={sub.retailer?.contact_phone}/>
            <Row label="Email" value={sub.retailer?.contact_email}/>
          </Section>

          <Section title="Subscription" icon={Calendar}>
            <Row label="Package" value={sub.package?.name || "—"}/>
            <Row label="Start Date" value={fmtDate(sub.start_date)}/>
            <Row label="End Date" value={fmtDate(sub.end_date)}/>
            <Row label="Validity" value={`${sub.validity_days} days`}/>
            <Row label="Inv Status" value={sub.invoice_status} highlight/>
          </Section>

          <Section title="Payment" icon={CreditCard}>
            <Row label="Amount" value={fmtCur(sub.amount)}/>
            <Row label="Payment ID" value={sub.payment_id}/>
            <Row label="Mode" value={payJson.payment_mode || "—"}/>
            <Row label="Zoho Inv" value={invJson.invoice_number || "—"}/>
            <Row label="Inv Date" value={fmtDate(sub.invoice_created_date)}/>
          </Section>

          <Section title="Company" icon={Package}>
            <Row label="Name" value={sub.company?.business_name}/>
            <Row label="Code" value={sub.company?.company_code} highlight/>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
        <Icon size={12} className="text-blue-600"/>
        <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">{title}</span>
      </div>
      <div className="px-4 py-3 space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-[11px] shrink-0 text-slate-400">{label}</span>
      <span className={`text-[12px] font-semibold text-right break-all ${highlight ? "text-blue-600" : "text-slate-800"}`}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ROOT: SubscribedUsersTable
// ─────────────────────────────────────────────────────────────────────────────
export default function SubscribedUsersTable({
  entityType,
  entityId,
}) {
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFlt, setStatusFlt] = useState("all");
  const [selected, setSelected] = useState(null);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const [expiringDays, setExpiringDays] = useState("");
const [startDate, setStartDate] = useState(null);
const [endDate, setEndDate] = useState(null);
const [searchInput, setSearchInput] = useState("");

useEffect(() => {
  const delay = setTimeout(() => {
    if (searchInput.length >= 4 || searchInput.length === 0) {
      setSearch(searchInput);
      setPage(1);
    }
  }, 500);

  return () => clearTimeout(delay);
}, [searchInput]);

  const cardConfig = {
    total:   { bg: "bg-blue-50",     border: "border-blue-200",   text: "text-blue-600",   iconBg: "bg-blue-600/20" },
    active:  { bg: "bg-emerald-50",  border: "border-emerald-200 text-emerald-500", text: "text-emerald-500", iconBg: "bg-emerald-500/20" },
    expired: { bg: "bg-red-50",      border: "border-red-200",    text: "text-red-500",    iconBg: "bg-red-500/20" },
    month:   { bg: "bg-purple-50",   border: "border-purple-200", text: "text-purple-600", iconBg: "bg-purple-600/20" }
  };

  const fetchData = async (pg = 1) => {
  try {
    setLoading(true);

    const params = new URLSearchParams({
      page: pg,
      per_page: 10,
    });

    // ROLE 2 COMPANY FILTER
    const role = localStorage.getItem("role");
    const companyId = localStorage.getItem("id");

    if (Number(role) === 2 && companyId) {
      params.append("company_id", companyId);
    }

    if (entityType && entityId) {
  params.append("retailer_id", entityId);
}

    // SEARCH
    if (search.trim()) {
      params.append("search_value", search.trim());
    }

    // STATUS FILTER
    if (statusFlt === "active") {
      params.append("subscription_type", "active");
    }

    if (statusFlt === "expired") {
      params.append("subscription_type", "expired");
    }

    // EXPIRING FILTER
    if (statusFlt === "expiring" && expiringDays) {
      params.append("subscription_type", "expiring");
      params.append("days", expiringDays);
    }

    // DATE RANGE
    if (startDate) {
      params.append(
        "created_from",
        startDate.toISOString().split("T")[0]
      );
    }

    if (endDate) {
      params.append(
        "created_to",
        endDate.toISOString().split("T")[0]
      );
    }

    const res = await fetch(
      `${BASE_URL}common/get-subscribed-users?${params.toString()}`
    );

    const json = await res.json();

    if (json.status) {
      setData(json.data || []);
      setCounts(json.counts || {});
      setTotalPages(json.pagination?.last_page || 1);
      setPage(json.pagination?.current_page || 1);
    }
  } catch (err) {
    console.error("Fetch error:", err);
  } finally {
    setLoading(false);
  }
};

  // Triggers reload correctly when filters/search changes or standard page changes
  useEffect(() => {
  fetchData(page);
}, [
  page,
  search,
  statusFlt,
  expiringDays,
  startDate,
  endDate,
]);

  const toggleSort = (key) => {
    setSortDir(sortKey === key && sortDir === "asc" ? "desc" : "asc");
    setSortKey(key);
  };

  const sorted = useMemo(() => {
  return [...data].sort((a, b) => {
    let va;
    let vb;

    switch (sortKey) {
      case "retailer":
        va = a.retailer?.business_name || "";
        vb = b.retailer?.business_name || "";
        break;

      case "package_id":
        va = a.package?.name || "";
        vb = b.package?.name || "";
        break;

      case "amount":
      case "balance":
        va = Number(a[sortKey] || 0);
        vb = Number(b[sortKey] || 0);
        break;

      default:
        va = a[sortKey] || "";
        vb = b[sortKey] || "";
    }

    if (typeof va === "string") {
      return sortDir === "asc"
        ? va.localeCompare(vb)
        : vb.localeCompare(va);
    }

    return sortDir === "asc" ? va - vb : vb - va;
  });
}, [data, sortKey, sortDir]);

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ChevronDown size={10} className="text-slate-400" />;
    return sortDir === "asc" ? <ChevronUp size={10} className="text-blue-600" /> : <ChevronDown size={10} className="text-blue-600" />;
  };

  return (
    <div className="space-y-4 font-['Plus_Jakarta_Sans','DM_Sans',sans-serif]">

      {/* ── STAT CARDS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total" value={counts.total_subscriptions ?? 0} icon={Shield} styles={cardConfig.total}/>
        <StatCard label="Active" value={counts.active_subscriptions ?? 0} icon={CheckCircle2} styles={cardConfig.active}/>
        <StatCard label="Expired" value={counts.expired_subscriptions ?? 0} icon={AlertTriangle} styles={cardConfig.expired}/>
        <StatCard label="This Month" value={counts.this_month_subscriptions ?? 0} icon={TrendingUp} styles={cardConfig.month}/>
      </div>

      {/* Expiry alerts */}
      {(counts.expiring_in_7_days > 0 || counts.today_expiring > 0) && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[12px] font-semibold bg-amber-50 border border-amber-200 text-amber-600">
          <AlertTriangle size={14}/>
          {counts.today_expiring > 0 && `${counts.today_expiring} expiring today · `}
          {counts.expiring_in_3_days > 0 && `${counts.expiring_in_3_days} in 3 days · `}
          {counts.expiring_in_7_days > 0 && `${counts.expiring_in_7_days} in 7 days`}
        </div>
      )}

      {/* ── MAIN TABLE CARD ── */}
      <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-[0_2px_16px_rgba(0,0,0,0.06)] min-h-[500px]">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-blue-50">
              <Users size={15} className="text-blue-600"/>
            </div>
            <div>
              <h2 className="text-[14px] font-black text-slate-800">Subscribed Users</h2>
              <p className="text-[11px] font-medium text-slate-400">{counts.total_subscriptions || 0} total subscriptions</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="flex items-center gap-1 flex-wrap">

                {/* ALL */}
                <button
                    onClick={() => {
                    setStatusFlt("all");
                    setExpiringDays("");
                    setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
                    statusFlt === "all"
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-slate-50 text-slate-600 border-slate-200"
                    }`}
                >
                    All
                </button>

                {/* ACTIVE */}
                <button
                    onClick={() => {
                    setStatusFlt("active");
                    setExpiringDays("");
                    setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
                    statusFlt === "active"
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-slate-50 text-slate-600 border-slate-200"
                    }`}
                >
                    Active
                </button>

                {/* EXPIRED */}
                <button
                    onClick={() => {
                    setStatusFlt("expired");
                    setExpiringDays("");
                    setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
                    statusFlt === "expired"
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-slate-50 text-slate-600 border-slate-200"
                    }`}
                >
                    Expired
                </button>

                <div className="flex items-center gap-2">

  <input
    type="number"
    min="1"
    placeholder="Expiring Days"
    value={expiringDays}
    onChange={(e) => {
      const value = e.target.value;

      setExpiringDays(value);
      setPage(1);

      if (value) {
        setStatusFlt("expiring");
      } else {
        setStatusFlt("all");
      }
    }}
    className="
      w-28 px-3 py-2
      rounded-xl border border-slate-200
      text-[12px] font-semibold
      outline-none
      focus:ring-2 focus:ring-amber-200
    "
  />

</div>
                </div>
            {/* Search */}
            <div className="relative">
                <Search
                    size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />

                <input
                    type="text"
                    placeholder="Search min 4 letters..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-9 pr-3 py-2 rounded-xl border text-[12px] font-semibold outline-none transition-all w-44 sm:w-52 border-slate-200 text-slate-800 focus:ring-2 focus:ring-blue-200"
                />
                </div>

                <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(start, end) => {
                        setStartDate(start);
                        setEndDate(end);
                        setPage(1);
                    }}
                    />

            {/* Refresh */}
            <button onClick={() => fetchData(page)} className="w-9 h-9 rounded-xl flex items-center justify-center border border-slate-200 hover:bg-slate-50 transition-all">
              <RefreshCw size={14} className="text-slate-400"/>
            </button>
          </div>
        </div>

        {/* Dynamic Display Handler */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin border-blue-600"/>
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-blue-50">
              <Shield size={24} className="text-blue-600 opacity-40"/>
            </div>
            <p className="text-[14px] font-bold text-slate-600">No subscriptions found</p>
            <p className="text-[12px] mt-1 text-slate-400">Try adjusting filters or search</p>
          </div>
        ) : (
          <>
            {/* ── DESKTOP TABLE ── */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    {[
                      { key: null, label: "Sr No" },
                      { key: "retailer",          label: "Retailer" },
                    //   { key: "subscription_code", label: "Sub Code" },
                      { key: "package_id",        label: "Package" },
                    //   { key: "start_date",        label: "Start" },
                      { key: "end_date",          label: "Valid Till" },
                      { key: "balance",           label: "Enroll Balance" },
                      { key: "amount",            label: "Amount" },
                      { key: "status",            label: "Status" },
                      { key: "last_used_date",        label: "Last used" },
                      { key: "action",            label: "Action" },
                    ].map(col => (
                      <th
                        key={col.label}
                        onClick={() => col.key && toggleSort(col.key)}
                        className={`p-[10px_14px] text-[12px] font-900 uppercase tracking-wider bg-slate-50 border-b border-slate-200 cursor-pointer select-none whitespace-nowrap ${sortKey === col.key ? "text-blue-600" : "text-slate-400"}`}>
                        <div className="flex items-center gap-1">
                          {col.label}
                          {col.key && <SortIcon k={col.key}/>}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((sub, index) => {
                    const payMode = (() => { try { return JSON.parse(sub.payment_json || "{}").payment_mode || "—"; } catch { return "—"; } })();
                    return (
                      <tr key={sub.id} onClick={() => setSelected(sub)} className="hover:bg-blue-50/30 transition-colors cursor-pointer border-b border-slate-50">
                        <td className="p-[12px_14px] text-[12px] font-bold text-slate-700">
                          {(page - 1) * 10 + index + 1}
                        </td>
                        <td className="p-[12px_14px] min-w-[180px]">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center font-black text-white text-[11px] shrink-0 bg-gradient-to-br from-blue-600 to-blue-700">
                              {initials(sub.retailer?.business_name)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[12px] font-bold truncate text-slate-800">{sub.retailer?.business_name || "—"}</p>
                              <p className="text-[12px] font-black text-blue-600">{sub.retailer?.company_code || "—"}</p>
                            </div>
                          </div>
                        </td>
        
                        <td className="p-[12px_14px]">
                          <p className="text-[12px] font-semibold text-slate-800">{sub.package?.name || "—"}</p>
                          {/* <p className="text-[12px] text-slate-400">{sub.company?.company_code}</p> */}
                           <p className="text-[11px] font-bold font-mono text-slate-600">{sub.subscription_code || "—"}</p>
                        </td>
                        {/* <td className="p-[12px_14px] text-[12px] font-semibold text-slate-700"></td> */}
                        <td className="p-[12px_14px]">
                          <p className="text-[12px] font-semibold text-slate-700">{fmtDate(sub.end_date)}</p>
                          <p className="text-[12px] text-slate-400">{sub.validity_days}d validity</p>
                          <p className="text-[12px] text-slate-400">purchased: {fmtDate(sub.created_at)}</p>
                        </td>
                        <td className="p-[12px_14px] min-w-[110px]"><BalanceBar balance={sub.balance || 0} max={sub.enroll_max || 0}/></td>
                        <td className="p-[12px_14px]">
                          <p className="text-[13px] font-black text-blue-600">{fmtCur(sub.amount)}</p>
                          <p className="text-[12px] text-slate-400">{payMode}</p>
                        </td>

                        
                        <td className="p-[12px_14px]"><StatusPill sub={sub}/></td>
                         <td className="p-[12px_14px]">
                            {sub.last_used_date ? (
                              <>
                                <p className="text-[12px] font-semibold text-slate-700">
                                  {fmtDate(sub.last_used_date)}
                                </p>

                                <p className="text-[11px] text-slate-400">
                                  {(() => {
                                    const today = new Date();
                                    const lastUsed = new Date(sub.last_used_date);

                                    today.setHours(0, 0, 0, 0);
                                    lastUsed.setHours(0, 0, 0, 0);

                                    const diff = Math.floor(
                                      (today - lastUsed) / 86400000
                                    );

                                    if (diff <= 0) return "Today";
                                    if (diff === 1) return "1d ago";

                                    return `${diff}d ago`;
                                  })()}
                                </p>
                              </>
                            ) : (
                              <p className="text-[12px] font-semibold text-slate-700">
                                Never Used
                              </p>
                            )}
                          </td>
                        <td className="p-[12px_14px]" onClick={e => e.stopPropagation()}>
                          <button onClick={() => setSelected(sub)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-black text-white transition-all hover:scale-105 active:scale-95 bg-gradient-to-br from-blue-600 to-blue-700 shadow-[0_3px_10px_rgba(37,99,235,0.2)]">
                            <Eye size={11}/> View
                          </button>
                        </td>
                       
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── MOBILE CARDS ── */}
            <div className="lg:hidden p-4 space-y-3">
              {sorted.map((sub) => (
                <div key={sub.id} onClick={() => setSelected(sub)} className="bg-white rounded-2xl border overflow-hidden cursor-pointer hover:shadow-md transition-shadow border-slate-200">
                  <div className={`h-1 ${daysLeft(sub.end_date) <= 7 ? "bg-amber-500" : "bg-emerald-500"}`}/>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white text-[13px] bg-gradient-to-br from-blue-600 to-blue-700">
                          {initials(sub.retailer?.business_name)}
                        </div>
                        <div>
                          <p className="text-[13px] font-bold text-slate-800">{sub.retailer?.business_name}</p>
                          <p className="text-[12px] font-black text-blue-600">{sub.retailer?.company_code}</p>
                        </div>
                      </div>
                      <StatusPill sub={sub}/>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3 text-[11px]">
                      <div>
                        <p className="text-slate-400">Package</p>
                        <p className="font-semibold text-slate-800">{sub.package?.name || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Amount</p>
                        <p className="font-black text-blue-600">{fmtCur(sub.amount)}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Start</p>
                        <p className="font-semibold text-slate-800">{fmtDate(sub.start_date)}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">End</p>
                        <p className="font-semibold text-slate-800">{fmtDate(sub.end_date)}</p>
                      </div>
                    </div>
                    <BalanceBar balance={sub.balance || 0} max={sub.enroll_max || 0}/>
                  </div>
                </div>
              ))}
            </div>

            {/* Custom Pagination */}
            {totalPages > 1 && (
            <div className="px-4 pb-4">
                <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(newPage) => {
                    setPage(newPage);
                }}
                />
            </div>
            )}
          </>
        )}
      </div>

      {/* Detail Drawer */}
      {selected && <DetailDrawer sub={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}